import * as React from 'react';
import {
  AlertCircle,
  AlertTriangle,
  Info,
  Bell,
  X,
  ChevronDown,
} from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '../primitives/alert';
import { Badge } from '../primitives/badge';
import { Button } from '../primitives/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../primitives/collapsible';
import { cn } from '../lib/utils';

/**
 * ISA-18.2 alarm severity levels.
 */
export type AlarmSeverity = 'critical' | 'major' | 'minor' | 'advisory';

export interface Alarm {
  id: string;
  message: string;
  severity: AlarmSeverity;
  acknowledged?: boolean;
  timestamp?: Date;
  source?: string;
}

export interface AlarmBannerProps {
  alarms: Alarm[];
  onAcknowledge?: (alarmId: string) => void;
  onDismiss?: (alarmId: string) => void;
  className?: string;
}

const severityConfig: Record<
  AlarmSeverity,
  {
    variant: 'destructive' | 'warning' | 'info' | 'default';
    icon: React.ComponentType<{ className?: string }>;
    badgeVariant: 'critical' | 'warning' | 'info' | 'muted';
    priority: number;
    label: string;
  }
> = {
  critical: {
    variant: 'destructive',
    icon: AlertCircle,
    badgeVariant: 'critical',
    priority: 0,
    label: 'Critical',
  },
  major: {
    variant: 'destructive',
    icon: AlertTriangle,
    badgeVariant: 'critical',
    priority: 1,
    label: 'Major',
  },
  minor: {
    variant: 'warning',
    icon: AlertTriangle,
    badgeVariant: 'warning',
    priority: 2,
    label: 'Minor',
  },
  advisory: {
    variant: 'info',
    icon: Info,
    badgeVariant: 'info',
    priority: 3,
    label: 'Advisory',
  },
};

/**
 * ISA-18.2 compliant alarm banner that summarizes active alarms and allows
 * operators to expand the list, acknowledge, or dismiss. Composes the `Alert`,
 * `Badge`, `Button`, and `Collapsible` primitives.
 *
 * @example
 * ```tsx
 * <AlarmBanner
 *   alarms={activeAlarms}
 *   onAcknowledge={ack}
 *   onDismiss={dismiss}
 * />
 * ```
 */
export const AlarmBanner = React.forwardRef<HTMLDivElement, AlarmBannerProps>(
  ({ alarms, onAcknowledge, onDismiss, className }, ref) => {
    const [open, setOpen] = React.useState(false);

    if (!alarms || alarms.length === 0) return null;

    // ISA-18.2 prioritization: order alarms by severity (highest priority
    // first; lower `priority` number == higher severity), then break ties by
    // most-recent timestamp so the freshest of equal-severity alarms surface
    // first. This ordering drives both the "highest" summary and the
    // expanded list below.
    const sortedAlarms = [...alarms].sort((a, b) => {
      const byPriority =
        severityConfig[a.severity].priority - severityConfig[b.severity].priority;
      if (byPriority !== 0) return byPriority;
      const at = a.timestamp ? a.timestamp.getTime() : 0;
      const bt = b.timestamp ? b.timestamp.getTime() : 0;
      return bt - at;
    });

    // Find highest severity
    const highest = sortedAlarms[0];
    if (!highest) return null;
    const topConfig = severityConfig[highest.severity];
    const Icon = topConfig.icon;
    const hasUnacked = alarms.some(
      (a) => !a.acknowledged && a.severity === 'critical'
    );

    return (
      <div ref={ref} className={className}>
        <Collapsible open={open} onOpenChange={setOpen}>
          <Alert
            variant={topConfig.variant}
            className={cn(hasUnacked && 'animate-pulse')}
          >
            <Icon className="h-4 w-4" />
            <div className="flex w-full items-start justify-between gap-3">
              <div className="flex-1">
                <AlertTitle className="flex items-center gap-2">
                  <span>
                    {alarms.length} {alarms.length === 1 ? 'Alarm' : 'Alarms'}
                  </span>
                  <Badge
                    variant={topConfig.badgeVariant}
                    data-alarm-severity={highest.severity}
                  >
                    {topConfig.label.toUpperCase()}
                  </Badge>
                  {hasUnacked && <Bell className="h-4 w-4 animate-bounce" />}
                </AlertTitle>
                <AlertDescription>
                  Highest: {highest.message}
                </AlertDescription>
              </div>
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="-mt-1"
                  aria-label={open ? 'Collapse alarms' : 'Expand alarms'}
                >
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 transition-transform',
                      open && 'rotate-180'
                    )}
                  />
                </Button>
              </CollapsibleTrigger>
            </div>
          </Alert>

          <CollapsibleContent className="mt-2 space-y-2">
            {sortedAlarms.map((alarm) => {
              const config = severityConfig[alarm.severity];
              const AlarmIcon = config.icon;
              return (
                <div
                  key={alarm.id}
                  className="flex items-start justify-between gap-3 rounded-md border bg-card px-3 py-2 shadow-sm"
                >
                  <div className="flex items-start gap-2">
                    <AlarmIcon
                      className={cn(
                        'mt-0.5 h-4 w-4 shrink-0',
                        alarm.severity === 'critical' &&
                          'text-[hsl(var(--alarm-critical))]',
                        alarm.severity === 'major' &&
                          'text-[hsl(var(--alarm-major))]',
                        alarm.severity === 'minor' &&
                          'text-[hsl(var(--alarm-minor))]',
                        alarm.severity === 'advisory' &&
                          'text-[hsl(var(--alarm-advisory))]'
                      )}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium">{alarm.message}</p>
                      <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                        {alarm.source && <span>{alarm.source}</span>}
                        {alarm.timestamp && (
                          <span>{alarm.timestamp.toLocaleTimeString()}</span>
                        )}
                        {alarm.acknowledged && (
                          <Badge variant="muted" className="h-4">
                            Acknowledged
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {!alarm.acknowledged && onAcknowledge && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onAcknowledge(alarm.id)}
                      >
                        Ack
                      </Button>
                    )}
                    {onDismiss && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onDismiss(alarm.id)}
                        aria-label="Dismiss alarm"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </CollapsibleContent>
        </Collapsible>
      </div>
    );
  }
);

AlarmBanner.displayName = 'AlarmBanner';
